import { CommonModule, formatDate } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { MenuItem, MessageService, SelectItem } from 'primeng/api';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { FieldsetModule } from 'primeng/fieldset';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { IftaLabelModule } from 'primeng/iftalabel';
import { TooltipModule } from 'primeng/tooltip';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageSearchPanelComponent } from '../../../shared/page-layout/page-search-panel.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { ShipmentService } from '../../shipment.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { LocationService } from '../../../location/location.service';
import { DirectoryMetrics, DeliveryDirectoryRow, DirectoryOption } from '../../models/shipment-directory.model';
import { formatOperationalState } from '../../utils/delivery-operation-progress.util';
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';
import { chargeCells, chargeColumns, exportPartyNames } from '../directory-export-columns';
import { LocaleService } from '../../../i18n/locale.service';
import { DirectoryFilterStateService } from '../directory-filter-state.service';
import { TenantScopeService } from '../../../company/tenant-scope.service';

type DirectoryOptionType = 'clients' | 'legacyCompanies' | 'crmClients' | 'crmAccounts' | 'crmContacts' | 'drivers' | 'vehicles' | 'projects';
type LazyOptionType = DirectoryOptionType | 'sourceLocations' | 'destinationLocations';

interface LazyOptionState {
    items: SelectItem<string>[];
    page: number;
    hasMore: boolean;
    loading: boolean;
    search: string;
    requestToken: number;
    filterTimer?: ReturnType<typeof setTimeout>;
}

interface DeliveryFilterModel {
    clients: string[];
    legacyCompanies: string[];
    crmClients: string[];
    crmAccounts: string[];
    crmContacts: string[];
    sourceLocations: string[];
    destinationLocations: string[];
    vehicles: string[];
    drivers: string[];
    dateRange: Date[];
    createdDateRange: Date[];
    shipmentNumber: string;
    deliveryNumber: string;
    waybillNumber: string;
    releaseNumber: string;
    referenceText: string;
    pickupCity: string;
    dropoffCity: string;
    searchTerm: string;
    logisticsProjectId: string | null;
    statuses: string[];
    assignmentState: string | null;
}

@Component({
    selector: 'app-delivery-company',
    templateUrl: './delivery-company.component.html',
    styleUrls: ['./delivery-company.component.css'],
    standalone: true,
    imports: [
        CommonModule, FormsModule, ToastModule, ProgressBarModule, BreadcrumbModule, FieldsetModule,
        MultiSelectModule, SelectModule, InputGroupModule, InputGroupAddonModule, IftaLabelModule,
        InputTextModule, ButtonModule, DatePickerModule, TableModule, TagModule, ChipModule,
        TooltipModule, PageShellComponent, PageHeroComponent, PageSearchPanelComponent,
        PageMetricStripComponent, PageDataPanelComponent
    ],
    providers: [MessageService]
})
export class DeliveryCompanyComponent implements OnInit, OnDestroy {
    protected readonly app_permissions = app_permissions;
    private readonly localeService = inject(LocaleService);
    private readonly tenantScope = inject(TenantScopeService);
    private readonly destroyRef = inject(DestroyRef);
    private currentCompanyId?: number;
    pageSize = 25;
    sortField = 'deliveryId';
    sortOrder = -1;
    readonly maxExportRows = 100;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    isLoading = false;
    isExporting = false;
    first = 0;
    lastTableEvent: TableLazyLoadEvent = {
        first: 0,
        rows: this.pageSize,
        sortField: 'deliveryId',
        sortOrder: -1
    };
    deliveries: DeliveryDirectoryRow[] = [];
    totalDeliveries = 0;
    assignedDeliveries = 0;
    unassignedDeliveries = 0;
    inTransitDeliveries = 0;
    completedDeliveries = 0;

    clientOptions: SelectItem<string>[] = [];
    legacyCompanyOptions: SelectItem<string>[] = [];
    crmClientOptions: SelectItem<string>[] = [];
    crmAccountOptions: SelectItem<string>[] = [];
    crmContactOptions: SelectItem<string>[] = [];
    sourceLocationOptions: SelectItem<string>[] = [];
    destinationLocationOptions: SelectItem<string>[] = [];
    vehicleOptions: SelectItem<string>[] = [];
    driverOptions: SelectItem<string>[] = [];
    logisticsProjectOptions: SelectItem<string>[] = [];

    filterModel: DeliveryFilterModel = this.emptyFilters();
    private appliedFilterModel: DeliveryFilterModel = this.emptyFilters();
    appliedChips: { key: keyof DeliveryFilterModel; label: string }[] = [];
    get assignmentOptions(): SelectItem<string | null>[] {
        this.localeService.locale();
        return [
            { label: this.t('pages.companyDeliveries.search.allAssignments'), value: null },
            { label: this.t('pages.companyDeliveries.search.fullyAssigned'), value: 'ASSIGNED' },
            { label: this.t('pages.companyDeliveries.search.needsAssignment'), value: 'UNASSIGNED' }
        ];
    }
    readonly deliveryStatusOptions: SelectItem<string>[] = [
        'PLANNED', 'ASSIGNED', 'ACCEPTED', 'IN_PROGRESS', 'DELIVERY_COMPLETE',
        'CUSTOMER_ACKNOWLEDGED', 'CANCELLED'
    ].map(value => ({ label: formatOperationalState(value), value }));

    private readonly optionStates: Record<LazyOptionType, LazyOptionState> = {
        clients: this.newOptionState(),
        legacyCompanies: this.newOptionState(),
        crmClients: this.newOptionState(),
        crmAccounts: this.newOptionState(),
        crmContacts: this.newOptionState(),
        sourceLocations: this.newOptionState(),
        destinationLocations: this.newOptionState(),
        vehicles: this.newOptionState(),
        drivers: this.newOptionState(),
        projects: this.newOptionState()
    };
    private directoryRequestToken = 0;

    constructor(
        private readonly shipmentService: ShipmentService,
        private readonly sessionStorageService: SessionStorageService,
        private readonly locationService: LocationService,
        private readonly router: Router,
        private readonly messageService: MessageService,
        private readonly filterState: DirectoryFilterStateService,
        public readonly permissionService: PermissionService
    ) { }

    
    t(key: string, fallbackOrParams?: string | Record<string, unknown>, params?: Record<string, unknown>): string {
        this.localeService.locale();
        if (typeof fallbackOrParams === 'string') {
            return this.localeService.translateIfPresent(key, fallbackOrParams, params);
        }
        return this.localeService.translate(key, fallbackOrParams);
    }

    currentPageReportTemplate(): string {
        this.localeService.locale();
        return this.t('pages.companyDeliveries.table.pageReport');
    }

    ngOnInit(): void {
        this.currentCompanyId = this.sessionStorageService.getCompany()?.id;
        this.restoreFilters();
        this.tenantScope.activeScope$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(scope => {
            if (scope?.type !== 'COMPANY' || scope.companyId === this.currentCompanyId) return;
            queueMicrotask(() => {
                if (this.sessionStorageService.getCompany()?.id !== scope.companyId) return;
                this.currentCompanyId = scope.companyId;
                this.filterModel = this.emptyFilters();
                this.appliedFilterModel = this.emptyFilters();
                this.appliedChips = [];
                this.sortField = 'deliveryId';
                this.sortOrder = -1;
                this.pageSize = 25;
                for (const type of Object.keys(this.optionStates) as LazyOptionType[]) {
                    const previous = this.optionStates[type];
                    if (previous.filterTimer) clearTimeout(previous.filterTimer);
                    this.optionStates[type] = this.newOptionState();
                    this.syncOptionItems(type);
                }
                this.restoreFilters();
                this.loadDeliveries({ first: 0, rows: this.pageSize, sortField: this.sortField, sortOrder: this.sortOrder });
            });
        });
        this.breadcrumbItems = [
            { label: 'Shipments', routerLink: '/shipment/company' },
            { label: 'Delivery Directory' }
        ];
    }

    ngOnDestroy(): void {
        Object.values(this.optionStates).forEach(state => {
            if (state.filterTimer) clearTimeout(state.filterTimer);
        });
    }

    loadDeliveries(event: TableLazyLoadEvent = this.lastTableEvent): void {
        const requestToken = ++this.directoryRequestToken;
        this.isLoading = true;
        this.lastTableEvent = event;
        this.first = event.first ?? 0;
        const size = event.rows ?? this.pageSize;
        this.pageSize = size;
        const page = Math.floor(this.first / size);
        const field = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
        const sort = `${field || 'deliveryId'},${(event.sortOrder ?? -1) > 0 ? 'asc' : 'desc'}`;
        this.sortField = field || 'deliveryId';
        this.sortOrder = (event.sortOrder ?? -1) > 0 ? 1 : -1;
        this.saveFilters();

        this.shipmentService.searchDeliveryDirectory(this.directorySearchParams(this.appliedFilterModel), { page, size, sort }).subscribe({
            next: response => {
                if (requestToken !== this.directoryRequestToken) return;
                this.deliveries = response.content ?? [];
                this.applyMetrics(response.metrics);
                this.isLoading = false;
            },
            error: () => {
                if (requestToken !== this.directoryRequestToken) return;
                this.deliveries = [];
                this.applyMetrics(undefined);
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Unable to load deliveries. Please try again.' });
            }
        });
    }

    refresh(): void {
        this.loadDeliveries(this.lastTableEvent);
    }

    applyFilters(): void {
        this.appliedFilterModel = this.cloneFilters(this.filterModel);
        this.appliedChips = this.buildFilterChips();
        this.first = 0;
        this.saveFilters();
        this.loadDeliveries({ ...this.lastTableEvent, first: 0, rows: this.lastTableEvent.rows ?? this.pageSize });
    }

    clearFilters(): void {
        this.filterModel = this.emptyFilters();
        this.applyFilters();
        this.filterState.clear('deliveries');
    }

    viewDelivery(row: DeliveryDirectoryRow): void {
        this.router.navigate(['/shipment/delivery/view', row.deliveryId]);
    }

    viewShipment(row: DeliveryDirectoryRow, advanced = false): void {
        this.router.navigate(advanced ? ['/shipment/view', row.shipmentId, 'advanced'] : ['/shipment/view', row.shipmentId]);
    }

    openShipmentDirectory(): void { this.router.navigate(['/shipment/company']); }
    createShipment(): void { this.router.navigate(['/shipment/create']); }

    exportExcel(): void {
        if (!this.hasValidExportDateRange()) return;
        this.isExporting = true;
        this.shipmentService.searchDeliveryDirectory({ ...this.directorySearchParams(this.appliedFilterModel), includeExportFields: 'true' }, {
            page: 0, size: this.maxExportRows, sort: `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`
        }).subscribe({
            next: response => {
                try {
                    const rows = (response.content ?? []).slice(0, this.maxExportRows);
                    if (!rows.length) {
                        this.messageService.add({ severity: 'warn', summary: 'No deliveries to export', detail: 'No deliveries match the selected filters.' });
                        return;
                    }
                    const exportRows = this.toExportRows(rows);
                    const worksheet = XLSX.utils.json_to_sheet(exportRows);
                    worksheet['!cols'] = Array.from({ length: Object.keys(exportRows[0]).length }, () => ({ wch: 24 }));
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Delivery Directory');
                    XLSX.writeFile(workbook, `delivery-directory-${this.exportDateStamp()}.xlsx`);
                    const limited = response.totalElements > this.maxExportRows;
                    this.messageService.add({
                        severity: limited ? 'warn' : 'success',
                        summary: limited ? 'Export limited to 100 deliveries' : 'Deliveries exported',
                        detail: limited ? 'Only the first 100 matching deliveries were exported.' : `${rows.length} deliver${rows.length === 1 ? 'y' : 'ies'} exported.`
                    });
                } finally {
                    this.isExporting = false;
                }
            },
            error: () => {
                this.isExporting = false;
                this.messageService.add({ severity: 'error', summary: 'Export failed', detail: 'Unable to retrieve the filtered deliveries for export.' });
            }
        });
    }

    openOptions(type: LazyOptionType): void {
        const state = this.optionStates[type];
        if (!state.items.length && !state.loading) this.loadOptionPage(type, 0);
    }

    filterOptions(type: LazyOptionType, value: string): void {
        const state = this.optionStates[type];
        if (state.filterTimer) clearTimeout(state.filterTimer);
        state.filterTimer = setTimeout(() => {
            const search = (value ?? '').trim();
            if (search === state.search) return;
            state.search = search;
            // Invalidate an older search immediately so it cannot populate the
            // selector while the debounce callback starts the replacement call.
            state.requestToken++;
            state.loading = false;
            state.page = 0;
            state.hasMore = true;
            state.items = this.selectedOptionItems(type);
            this.syncOptionItems(type);
            this.loadOptionPage(type, 0);
        }, 300);
    }

    loadNextOptions(type: LazyOptionType, event: { last?: number }): void {
        const state = this.optionStates[type];
        if (!state.hasMore || state.loading || (event.last ?? 0) < Math.max(0, state.items.length - 4)) return;
        this.loadOptionPage(type, state.page + 1);
    }

    getStatusSeverity(status?: string): string {
        const normalized = (status || '').toUpperCase();
        if (['COMPLETED', 'DELIVERED', 'DELIVERY_COMPLETE', 'CUSTOMER_ACKNOWLEDGED', 'CLOSED'].includes(normalized)) return 'success';
        if (['IN_PROGRESS', 'IN_TRANSIT'].includes(normalized)) return 'warn';
        if (['CANCELLED', 'EXCEPTION', 'JOB_DRIVER_REJECTED', 'JOB_DRIVER_CANCELLED'].includes(normalized)) return 'danger';
        return normalized ? 'info' : 'secondary';
    }

    formatDateTime(timestamp?: number): string {
        return timestamp ? formatDate(new Date(timestamp), 'dd-MMM-yyyy HH:mm', 'en-US') : '';
    }

    deliveryMetrics(): PageMetric[] {
        this.localeService.locale();
        return [
            { label: this.t('pages.companyDeliveries.metrics.totalDeliveries'), value: this.totalDeliveries, hint: this.t('pages.companyDeliveries.metrics.totalDeliveriesHint'), tone: 'info' },
            { label: this.t('pages.companyDeliveries.metrics.needsAssignment'), value: this.unassignedDeliveries, hint: this.t('pages.companyDeliveries.metrics.fullyAssignedCount', { count: this.assignedDeliveries }), tone: this.unassignedDeliveries ? 'warn' : 'success' },
            { label: this.t('pages.companyDeliveries.metrics.inTransit'), value: this.inTransitDeliveries, hint: this.t('pages.companyDeliveries.metrics.inTransitHint'), tone: 'warn' },
            { label: this.t('pages.companyDeliveries.metrics.completed'), value: this.completedDeliveries, hint: this.t('pages.companyDeliveries.metrics.completedHint'), tone: 'success' }
        ];
    }

    activeFilterCount(): number {
        return this.appliedChips.length;
    }

    resultText(): string {
        this.localeService.locale();
        return this.t('pages.companyDeliveries.results.deliveries', { count: this.totalDeliveries });
    }
    formatStatusLabel(status?: string): string { return formatOperationalState(status); }
    deliveryLifecycleStatus(row: DeliveryDirectoryRow): string { return row.operation?.status || row.workflowState || row.jobStatus || 'PLANNED'; }
    deliveryProgress(row: DeliveryDirectoryRow): number { return row.operation?.percent ?? 0; }
    deliveryProgressCaption(row: DeliveryDirectoryRow): string { return row.operation?.caption || 'Delivery assignment pending'; }
    latestOperationalUpdate(row: DeliveryDirectoryRow): number | undefined { return row.operation?.latestUpdatedAt || row.workflowUpdatedAt || row.scheduledTime; }
    payloadSummary(row: DeliveryDirectoryRow): string { return row.cargoSummary || 'Payload details pending'; }
    waybillNumbers(row: DeliveryDirectoryRow): string[] { return row.operation?.waybillNumbers ?? []; }

    linkedReference(row: DeliveryDirectoryRow): string {
        if (row.primaryTransportReleaseNumber) return `Release ${row.primaryTransportReleaseNumber}`;
        if (row.crossDockOrchestrationNumber) return `Cross-dock ${row.crossDockOrchestrationNumber}`;
        return row.externalReference || row.shipmentExternalReference || 'No linked release';
    }

    private loadOptionPage(type: LazyOptionType, page: number): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        const state = this.optionStates[type];
        if (!companyId || state.loading || (page > 0 && !state.hasMore)) return;
        const requestToken = ++state.requestToken;
        state.loading = true;
        const applyResponse = (incoming: SelectItem<string>[], hasMore: boolean) => {
            if (requestToken !== state.requestToken) return;
            state.items = this.mergeOptionItems(page === 0 ? this.selectedOptionItems(type) : state.items, incoming);
            state.page = page;
            state.hasMore = hasMore;
            this.syncOptionItems(type);
        };
        const onError = () => {
            if (requestToken !== state.requestToken) return;
            state.loading = false;
            this.messageService.add({ severity: 'warn', summary: 'Options unavailable', detail: 'Filter options could not be loaded.' });
        };
        const onComplete = () => { if (requestToken === state.requestToken) state.loading = false; };

        if (type === 'sourceLocations' || type === 'destinationLocations') {
            this.locationService.getAllComboData(state.search, page, 25, 'en-US', companyId).subscribe({
                next: response => applyResponse((response.items ?? []).map(location => ({ label: location.name, value: String(location.id) })), response.hasMore),
                error: onError,
                complete: onComplete
            });
            return;
        }

        this.shipmentService.getDirectoryOptions(type, companyId, state.search, page, 25).subscribe({
            next: response => applyResponse((response.items ?? []).map((option: DirectoryOption) => ({ label: option.label, value: String(option.id) })), response.hasMore),
            error: onError,
            complete: onComplete
        });
    }

    private selectedOptionItems(type: LazyOptionType): SelectItem<string>[] {
        const selected = type === 'projects'
            ? (this.filterModel.logisticsProjectId ? [this.filterModel.logisticsProjectId] : [])
            : this.filterModel[type] ?? [];
        const existing = this.optionStates[type].items;
        return selected.map(value => existing.find(option => option.value === value) ?? { label: value, value });
    }

    private mergeOptionItems(existing: SelectItem<string>[], incoming: SelectItem<string>[]): SelectItem<string>[] {
        const values = new Map<string, SelectItem<string>>();
        [...existing, ...incoming].forEach(item => values.set(item.value, item));
        return Array.from(values.values());
    }

    private syncOptionItems(type: LazyOptionType): void {
        const items = this.optionStates[type].items;
        switch (type) {
            case 'clients': this.clientOptions = items; break;
            case 'legacyCompanies': this.legacyCompanyOptions = items; break;
            case 'crmClients': this.crmClientOptions = items; break;
            case 'crmAccounts': this.crmAccountOptions = items; break;
            case 'crmContacts': this.crmContactOptions = items; break;
            case 'sourceLocations': this.sourceLocationOptions = items; break;
            case 'destinationLocations': this.destinationLocationOptions = items; break;
            case 'vehicles': this.vehicleOptions = items; break;
            case 'drivers': this.driverOptions = items; break;
            case 'projects': this.logisticsProjectOptions = items; break;
        }
    }

    private directorySearchParams(f: DeliveryFilterModel): Record<string, string> {
        const params: Record<string, string> = {};
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (companyId) params['owningCompanyId'] = String(companyId);
        this.setParam(params, 'shipmentNumber', f.shipmentNumber); this.setParam(params, 'deliveryNumber', f.deliveryNumber);
        this.setParam(params, 'waybillNumber', f.waybillNumber); this.setParam(params, 'searchText', f.searchTerm);
        this.setParam(params, 'releaseNumber', f.releaseNumber); this.setParam(params, 'referenceText', f.referenceText);
        this.setParam(params, 'pickupCity', f.pickupCity); this.setParam(params, 'dropoffCity', f.dropoffCity);
        this.setParam(params, 'legacyCustomerList', f.clients.join(',')); this.setParam(params, 'sourceLocationList', f.sourceLocations.join(','));
        this.setParam(params, 'legacyCompanyList', f.legacyCompanies.join(','));
        this.setParam(params, 'crmClientList', f.crmClients.join(','));
        this.setParam(params, 'crmAccountList', f.crmAccounts.join(','));
        this.setParam(params, 'crmContactList', f.crmContacts.join(','));
        this.setParam(params, 'destinationLocationList', f.destinationLocations.join(',')); this.setParam(params, 'vehicleList', f.vehicles.join(','));
        this.setParam(params, 'driverList', f.drivers.join(',')); this.setParam(params, 'logisticsProjectId', f.logisticsProjectId);
        this.setParam(params, 'deliveryStatus', f.statuses.join(',')); this.setParam(params, 'assignmentState', f.assignmentState);
        if (f.dateRange[0]) params['scheduledDateFrom'] = formatDate(f.dateRange[0], 'yyyy-MM-dd', 'en-US');
        if (f.dateRange[1]) params['scheduledDateTo'] = formatDate(f.dateRange[1], 'yyyy-MM-dd', 'en-US');
        if (f.createdDateRange[0]) params['createdDateFrom'] = formatDate(f.createdDateRange[0], 'yyyy-MM-dd', 'en-US');
        if (f.createdDateRange[1]) params['createdDateTo'] = formatDate(f.createdDateRange[1], 'yyyy-MM-dd', 'en-US');
        return params;
    }

    private applyMetrics(metrics?: DirectoryMetrics): void {
        const values = metrics ?? { total: 0, booked: 0, inTransit: 0, completed: 0, assigned: 0, unassigned: 0 };
        this.totalDeliveries = values.total; this.assignedDeliveries = values.assigned; this.unassignedDeliveries = values.unassigned;
        this.inTransitDeliveries = values.inTransit; this.completedDeliveries = values.completed;
    }

    private hasValidExportDateRange(): boolean {
        const [start, end] = this.appliedFilterModel.dateRange;
        if (!(start instanceof Date) || !(end instanceof Date) || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            this.messageService.add({ severity: 'warn', summary: 'Complete date range required', detail: 'Select both scheduled dates before exporting.' });
            return false;
        }
        if (end.getTime() < start.getTime() || end.getTime() - start.getTime() > 31 * 24 * 60 * 60 * 1000) {
            this.messageService.add({ severity: 'warn', summary: 'Invalid date range', detail: 'Exports are limited to a one-month scheduled date range.' });
            return false;
        }
        return true;
    }

    private toExportRows(rows: DeliveryDirectoryRow[]): Record<string, string | number>[] {
        const columns = chargeColumns(rows);
        const buyingCurrencies = [...new Set(rows.filter(row => row.assignedBuyingCost != null)
            .map(row => row.assignedBuyingCurrencyCode || row.currencyCode || 'SAR'))].sort();
        const bulkCurrencies = [...new Set(rows.flatMap(row => Object.keys(row.postedBulkCostByCurrency ?? {})))].sort();
        return rows.map(row => ({
            'Shipment Reference': row.shipmentExternalReference || row.customerOrderNumber || row.shipmentNumber || '',
            'Delivery Reference': row.externalReference || row.deliveryNumber || '', 'Customer Reference': row.customerOrderNumber || '',
            ...exportPartyNames(row), 'Pickup Location': row.sourceLocation?.name || '', 'Pickup Address': row.sourceLocation?.address || '',
            'Drop-off Location': row.destinationLocation?.name || '', 'Drop-off Address': row.destinationLocation?.address || '',
            'Waybill Number': this.waybillNumbers(row).join(', '), 'Scheduled Time': this.formatDateTime(row.scheduledTime),
            'Booking Option': this.formatStatusLabel(row.bookingOption), Driver: row.driver?.label || '', Vehicle: row.vehicle?.label || '',
            'Delivery Status': this.formatStatusLabel(this.deliveryLifecycleStatus(row)), 'Delivery Progress': `${this.deliveryProgress(row)}%`,
            'Cargo Description': this.payloadSummary(row), 'Related Reference': this.linkedReference(row), 'Last Updated': this.formatDateTime(this.latestOperationalUpdate(row)),
            ...Object.fromEntries((buyingCurrencies.length ? buyingCurrencies : ['']).map(currency => [
                currency ? `Assigned Buying Cost (${currency})` : 'Assigned Buying Cost',
                row.assignedBuyingCost != null && (row.assignedBuyingCurrencyCode || row.currencyCode || 'SAR') === currency
                    ? row.assignedBuyingCost : ''
            ])),
            ...Object.fromEntries((bulkCurrencies.length ? bulkCurrencies : ['']).map(currency => [
                currency ? `Posted Bulk Cost (${currency})` : 'Posted Bulk Cost',
                currency ? row.postedBulkCostByCurrency?.[currency] ?? '' : ''
            ])),
            ...chargeCells(row, columns)
        }));
    }

    removeFilter(key: keyof DeliveryFilterModel): void {
        this.filterModel = this.cloneFilters(this.appliedFilterModel);
        const empty = this.emptyFilters();
        (this.filterModel as any)[key] = empty[key];
        this.applyFilters();
    }

    private buildFilterChips(): { key: keyof DeliveryFilterModel; label: string }[] {
        const f = this.appliedFilterModel;
        const chips: { key: keyof DeliveryFilterModel; label: string }[] = [];
        const add = (key: keyof DeliveryFilterModel, title: string, value: string | null) => {
            if (value) chips.push({ key, label: `${title}: ${value}` });
        };
        const pick = (type: LazyOptionType, ids: string[]) => ids.map(id =>
            this.optionStates[type].items.find(item => item.value === id)?.label ?? id).join(', ');
        add('searchTerm', this.t('pages.companyDeliveries.search.search'), f.searchTerm.trim());
        add('shipmentNumber', this.t('pages.companyDeliveries.search.shipmentNumber'), f.shipmentNumber.trim());
        add('deliveryNumber', this.t('pages.companyDeliveries.search.deliveryNumber'), f.deliveryNumber.trim());
        add('waybillNumber', this.t('pages.companyDeliveries.search.waybillNumber'), f.waybillNumber.trim());
        add('releaseNumber', this.t('pages.companyDeliveries.search.releaseNumber'), f.releaseNumber.trim());
        add('referenceText', this.t('pages.companyDeliveries.search.referenceText'), f.referenceText.trim());
        for (const [key, titleKey] of [['clients', 'legacyCustomers'], ['legacyCompanies', 'legacyCompanies'],
            ['crmClients', 'crmClients'], ['crmAccounts', 'crmAccounts'], ['crmContacts', 'crmContacts'],
            ['drivers', 'driver'], ['vehicles', 'vehicle'], ['sourceLocations', 'sourceLocation'],
            ['destinationLocations', 'destinationLocation']] as [Exclude<LazyOptionType, 'projects'>, string][]) {
            const ids = f[key] as string[];
            if (ids.length) add(key, this.t(`pages.companyDeliveries.search.${titleKey}`), pick(key, ids));
        }
        add('logisticsProjectId', this.t('pages.companyDeliveries.search.logisticsProject'), f.logisticsProjectId ? pick('projects', [f.logisticsProjectId]) : null);
        add('statuses', this.t('pages.companyDeliveries.search.deliveryStatus'), f.statuses.join(', '));
        add('assignmentState', this.t('pages.companyDeliveries.search.assignmentReadiness'), f.assignmentState);
        add('pickupCity', this.t('pages.companyDeliveries.search.pickupCity'), f.pickupCity.trim());
        add('dropoffCity', this.t('pages.companyDeliveries.search.dropoffCity'), f.dropoffCity.trim());
        for (const [key, titleKey] of [['dateRange', 'scheduledDate'], ['createdDateRange', 'createdDateRange']] as const) {
            const range = f[key];
            if (range.length) add(key, this.t(`pages.companyDeliveries.search.${titleKey}`), range.map(date => date && formatDate(date, 'yyyy-MM-dd', 'en-US')).filter(Boolean).join(' – '));
        }
        return chips;
    }

    private cloneFilters(f: DeliveryFilterModel): DeliveryFilterModel {
        return { ...f, clients: [...f.clients], legacyCompanies: [...f.legacyCompanies],
            crmClients: [...f.crmClients], crmAccounts: [...f.crmAccounts], crmContacts: [...f.crmContacts],
            sourceLocations: [...f.sourceLocations], destinationLocations: [...f.destinationLocations],
            vehicles: [...f.vehicles], drivers: [...f.drivers], statuses: [...f.statuses],
            dateRange: [...f.dateRange], createdDateRange: [...f.createdDateRange] };
    }

    private saveFilters(): void {
        const selectedOptions: Record<string, SelectItem<string>[]> = {};
        for (const type of Object.keys(this.optionStates) as LazyOptionType[]) {
            const selected = type === 'projects'
                ? (this.appliedFilterModel.logisticsProjectId ? [this.appliedFilterModel.logisticsProjectId] : [])
                : this.appliedFilterModel[type] as string[];
            selectedOptions[type] = this.optionStates[type].items.filter(item => selected.includes(item.value));
        }
        const dates = (range: Date[]) => range.map(date => date ? formatDate(date, 'yyyy-MM-dd', 'en-US') : null);
        this.filterState.save('deliveries', {
            filters: { ...this.appliedFilterModel, dateRange: dates(this.appliedFilterModel.dateRange),
                createdDateRange: dates(this.appliedFilterModel.createdDateRange) },
            options: selectedOptions, sortField: this.sortField, sortOrder: this.sortOrder, rows: this.pageSize
        });
    }

    private restoreFilters(): void {
        const saved = this.filterState.load('deliveries') as {
            filters?: any; options?: Record<string, SelectItem<string>[]>;
            sortField?: string; sortOrder?: number; rows?: number;
        } | null;
        const raw = saved?.filters;
        if (!raw || typeof raw !== 'object') return;
        const f = this.emptyFilters();
        const string = (value: unknown) => typeof value === 'string' ? value.slice(0, 250) : '';
        const ids = (value: unknown): string[] => Array.isArray(value)
            ? value.filter(item => typeof item === 'string' && /^\d+$/.test(item)).slice(0, 50) : [];
        const dates = (value: unknown): Date[] => Array.isArray(value) ? value.slice(0, 2).map(item => {
            if (typeof item !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(item)) return null;
            const [year, month, day] = item.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
        }) as Date[] : [];
        for (const key of ['shipmentNumber', 'deliveryNumber', 'waybillNumber', 'releaseNumber', 'referenceText', 'searchTerm', 'pickupCity', 'dropoffCity'] as const) f[key] = string(raw[key]);
        for (const key of ['clients', 'legacyCompanies', 'crmClients', 'crmAccounts', 'crmContacts', 'sourceLocations', 'destinationLocations', 'vehicles', 'drivers'] as const) f[key] = ids(raw[key]);
        f.logisticsProjectId = ids([raw.logisticsProjectId])[0] ?? null;
        f.statuses = Array.isArray(raw.statuses) ? raw.statuses.filter((item: unknown) => this.deliveryStatusOptions.some(option => option.value === item)) : [];
        f.assignmentState = ['ASSIGNED', 'UNASSIGNED'].includes(raw.assignmentState) ? raw.assignmentState : null;
        f.dateRange = dates(raw.dateRange);
        f.createdDateRange = dates(raw.createdDateRange);
        this.filterModel = f;
        this.appliedFilterModel = this.cloneFilters(f);
        for (const type of Object.keys(this.optionStates) as LazyOptionType[]) {
            const options = saved?.options?.[type];
            this.optionStates[type].items = Array.isArray(options) ? options.filter((item: SelectItem<string>) =>
                typeof item.label === 'string' && typeof item.value === 'string').slice(0, 50) : [];
            this.syncOptionItems(type);
        }
        const savedSortField = saved?.sortField ?? '';
        this.sortField = ['deliveryId', 'deliveryNumber', 'shipmentNumber', 'scheduledTime', 'jobStatus'].includes(savedSortField) ? savedSortField : 'deliveryId';
        this.sortOrder = saved?.sortOrder === 1 ? 1 : -1;
        const savedRows = saved?.rows ?? 25;
        this.pageSize = [10, 25, 50].includes(savedRows) ? savedRows : 25;
        this.lastTableEvent = { first: 0, rows: this.pageSize, sortField: this.sortField, sortOrder: this.sortOrder };
        this.appliedChips = this.buildFilterChips();
    }

    private emptyFilters(): DeliveryFilterModel {
        return { clients: [], legacyCompanies: [], crmClients: [], crmAccounts: [], crmContacts: [],
            sourceLocations: [], destinationLocations: [], vehicles: [], drivers: [], dateRange: [], createdDateRange: [],
            shipmentNumber: '', deliveryNumber: '', waybillNumber: '', releaseNumber: '', referenceText: '',
            pickupCity: '', dropoffCity: '', searchTerm: '', logisticsProjectId: null, statuses: [], assignmentState: null };
    }
    private newOptionState(): LazyOptionState { return { items: [], page: 0, hasMore: true, loading: false, search: '', requestToken: 0 }; }
    private setParam(params: Record<string, string>, key: string, value?: string | null): void { if (value?.trim()) params[key] = value.trim(); }
    private exportDateStamp(): string { return formatDate(new Date(), 'yyyyMMdd-HHmm', 'en-US'); }
}
